
import React from "react";
import {
  Award,
  Eye,
  ShieldCheck,
  Heart,
  ArrowRight,
  ArrowUpRight,
  Building2,
  Ruler,
  ClipboardCheck,
  CheckCircle2,
  Paintbrush,
  Users,
  MapPin,
} from "lucide-react";

import AboutImage from "../../assests/team/herosection/cubehero3.webp";

const coreValues = [
  {
    icon: Award,
    title: "Quality",
    desc: "We focus on good materials, careful work and clean finishes.",
  },
  {
    icon: Eye,
    title: "Smart Design",
    desc: "We plan spaces to look beautiful and remain practical for everyday use.",
  },
  {
    icon: ShieldCheck,
    title: "Reliable Work",
    desc: "We aim for clear communication, proper planning and organized execution.",
  },
  {
    icon: Heart,
    title: "Customer First",
    desc: "We listen to your ideas, needs and budget when planning your project.",
  },
];

const highlights = [
  {
    icon: Building2,
    title: "Interior Design & Construction",
    desc: "We plan and build spaces that suit your needs, style and budget.",
  },
  {
    icon: Paintbrush,
    title: "Painting & Finishing",
    desc: "We refresh homes, offices and other spaces with suitable colours and finishes.",
  },
  {
    icon: ClipboardCheck,
    title: "Complete Project Solutions",
    desc: "We coordinate design, construction and painting to make your project easier to manage.",
  },
];

const benefits = [
  "Clear project planning",
  "Careful material selection",
  "Regular work updates",
  "Interior design and construction services",
  "Professional painting and finishing solutions",
  "Final checks before project handover",
];

const operatingPrinciples = [
  "Plan before starting work",
  "Coordinate each project stage",
  "Pay attention to materials and details",
  "Check the final results before handover",
];

const locations = [
  {
    name: "Cube4Spaces",
    subtitle: "Interior Design & Construction",
    address: "Cube 4 Spaces, Coimbatore",
    mapSrc:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3915.4859452144906!2d76.99992007584065!3d11.077114653573846!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba8f7d22a2e74b7%3A0x1cf0c55fe95cc923!2sCube%204%20spaces!5e0!3m2!1sen!2sin!4v1793006423105!5m2!1sen!2sin",
  },
  {
    name: "Paint Studio",
    subtitle: "Painting & Finishing Services",
    address: "Paint Studio, Coimbatore",
    mapSrc:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3915.4858046386466!2d76.99992007569429!3d11.077125160721295!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba8f72b34a6d09d%3A0x8a7a44f1274a232!2sPaint%20Studio!5e0!3m2!1sen!2sin!4v1793006480446!5m2!1sen!2sin",
  },
];

export default function About() {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.location.href = "/contact/";
    }
  };

  return (
    <section
      id="about"
      aria-labelledby="about-heading"
      className="relative isolate w-full overflow-hidden bg-slate-50 font-['DM_Sans',sans-serif] text-slate-900"
    >
      {/* Background decoration */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-0 top-0 -z-10 h-64 w-64 rounded-full bg-indigo-200/30 blur-[100px] sm:h-96 sm:w-96 lg:h-[500px] lg:w-[500px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 right-0 -z-10 h-64 w-64 rounded-full bg-slate-300/40 blur-[100px] sm:h-96 sm:w-96 lg:h-[500px] lg:w-[500px]"
      />

      <div className="mx-auto w-full max-w-[1440px] px-5 py-14 sm:px-8 sm:py-16 md:px-10 md:py-20 lg:px-12 lg:py-24 xl:px-16 xl:py-28">
        {/* HERO / ABOUT HEADER */}
        <div className="mb-12 grid grid-cols-1 items-center gap-8 sm:mb-16 lg:grid-cols-[minmax(0,1.1fr)_minmax(300px,0.9fr)] lg:gap-14 xl:mb-20">
          <div className="min-w-0">
            <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-white px-4 py-2 font-['Poppins',sans-serif] text-[10px] font-semibold uppercase tracking-[0.17em] text-indigo-700 shadow-sm sm:mb-6 sm:text-xs">
              About Cube4Spaces & Paint Studio
            </span>

            <h1
              id="about-heading"
              className="max-w-4xl font-['Poppins',sans-serif] text-[clamp(2.3rem,5.5vw,5rem)] font-semibold leading-[1.12] tracking-[-0.045em] text-slate-950"
            >
              We Design, Build and{" "}
              <span className="text-indigo-600">Paint Your Spaces.</span>
            </h1>
          </div>

          <div className="min-w-0 lg:ml-auto lg:max-w-xl">
            <p className="text-sm leading-7 text-slate-600 sm:text-base sm:leading-8 lg:text-lg">
              Cube4Spaces and Paint Studio work together to help you create
              beautiful, comfortable and well-finished spaces. From interior
              design and construction to home painting and waterproofing, we
              offer services to improve your home, office or commercial
              property.
            </p>

            <button
              type="button"
              onClick={() => scrollToSection("contact")}
              className="mt-6 inline-flex min-h-12 w-full items-center justify-center gap-3 rounded-lg bg-slate-950 px-6 py-3.5 font-['Poppins',sans-serif] text-sm font-semibold text-white transition-colors duration-300 hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-indigo-600 sm:mt-8 sm:w-auto sm:px-8"
            >
              Start Your Project
              <ArrowRight size={18} strokeWidth={1.7} />
            </button>
          </div>
        </div>

        {/* OUR STORY */}
        <div className="mb-14 grid min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm sm:mb-16 sm:rounded-3xl lg:grid-cols-2 lg:rounded-[2rem]">
          <div className="relative min-h-[260px] overflow-hidden sm:min-h-[360px] lg:min-h-[520px]">
            <img
              src={AboutImage}
              alt="Interior design and finished spaces by Cube4Spaces"
              loading="lazy"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/10 to-transparent" />

            <div className="absolute bottom-5 left-5 right-5 flex flex-wrap gap-2 sm:bottom-8 sm:left-8 sm:right-8">
              {["Design", "Build", "Paint", "Finish"].map((item) => (
                <span
                  key={item}
                  className="rounded-full bg-white/95 px-3 py-1.5 font-['Poppins',sans-serif] text-[10px] font-semibold uppercase tracking-wider text-indigo-700 shadow-sm sm:px-4 sm:py-2 sm:text-xs"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>

          <div className="flex min-w-0 flex-col justify-center p-5 sm:p-8 md:p-10 lg:p-12 xl:p-14">
            <span className="font-['Poppins',sans-serif] text-[10px] font-semibold uppercase tracking-[0.2em] text-indigo-600 sm:text-xs">
              Our Story
            </span>

            <h2 className="mt-3 font-['Poppins',sans-serif] text-2xl font-semibold leading-tight tracking-tight text-slate-950 sm:mt-4 sm:text-3xl lg:text-4xl">
              Everything Your Space Needs, in One Place.
            </h2>

            <p className="mt-4 text-sm leading-7 text-slate-600 sm:mt-6 sm:text-base sm:leading-8">
              Cube4Spaces focuses on interior design, construction and
              complete project solutions. Paint Studio specializes in
              interior and exterior painting, waterproofing and decorative
              finishes.
            </p>

            <p className="mt-4 text-sm leading-7 text-slate-600 sm:text-base sm:leading-8">
              Together, we help you manage your project from the first design
              to the final coat of paint. Our goal is to make the process
              simple, keep the work well planned and deliver a finish you can
              feel proud of.
            </p>

            <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 xl:grid-cols-1">
              {highlights.map((item) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.title}
                    className="flex min-w-0 items-start gap-4 border-t border-slate-100 pt-4 first:border-t-0 first:pt-0 sm:first:border-t sm:first:pt-4"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center text-slate-700 sm:h-11 sm:w-11">
                      <Icon size={23} strokeWidth={1.5} aria-hidden="true" />
                    </div>

                    <div className="min-w-0">
                      <h3 className="font-['Poppins',sans-serif] text-sm font-semibold leading-snug text-slate-950 sm:text-base">
                        {item.title}
                      </h3>
                      <p className="mt-1.5 text-xs leading-6 text-slate-500 sm:text-sm">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* OUR MISSION */}
        <div className="mb-14 grid grid-cols-1 gap-5 sm:mb-16 sm:gap-6 lg:grid-cols-2 lg:gap-8">
          <article className="min-w-0 rounded-2xl border border-slate-200 bg-white p-6 sm:rounded-3xl sm:p-8 md:p-10 lg:p-12">
            <span className="font-['Poppins',sans-serif] text-[10px] font-semibold uppercase tracking-[0.2em] text-indigo-600 sm:text-xs">
              Our Mission
            </span>

            <h3 className="mt-3 font-['Poppins',sans-serif] text-2xl font-semibold leading-tight tracking-tight text-slate-950 sm:mt-4 sm:text-3xl lg:text-4xl">
              Beautiful Spaces That Work for You.
            </h3>

            <p className="mt-4 text-sm leading-7 text-slate-600 sm:mt-6 sm:text-base sm:leading-8">
              Our goal is to create spaces that look good, feel comfortable
              and work well every day. We focus on practical designs, careful
              planning, suitable materials and quality finishing.
            </p>
          </article>

          <article className="min-w-0 rounded-2xl border border-slate-200 bg-white p-6 sm:rounded-3xl sm:p-8 md:p-10 lg:p-12">
            <span className="font-['Poppins',sans-serif] text-[10px] font-semibold uppercase tracking-[0.2em] text-indigo-600 sm:text-xs">
              Why Choose Us?
            </span>

            <h3 className="mt-3 font-['Poppins',sans-serif] text-2xl font-semibold leading-tight tracking-tight text-slate-950 sm:mt-4 sm:text-3xl lg:text-4xl">
              One Team. More Ways to Improve Your Space.
            </h3>

            <p className="mt-4 text-sm leading-7 text-slate-600 sm:mt-6 sm:text-base sm:leading-8">
              With Cube4Spaces and Paint Studio, you can find interior design,
              construction and painting services in one place. Our coordinated
              approach helps make project planning and execution easier.
            </p>

            <ul className="mt-6 space-y-3 sm:mt-7">
              {benefits.map((benefit) => (
                <li
                  key={benefit}
                  className="flex items-start gap-3 text-sm leading-6 text-slate-700 sm:text-base"
                >
                  <CheckCircle2
                    size={18}
                    strokeWidth={1.6}
                    className="mt-0.5 shrink-0 text-indigo-600"
                    aria-hidden="true"
                  />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </article>
        </div>

        {/* CORE VALUES */}
        <div className="mb-14 rounded-2xl border border-slate-200 bg-white p-6 sm:mb-16 sm:rounded-3xl sm:p-8 md:p-10 lg:p-12">
          <div className="mb-8 grid grid-cols-1 gap-4 sm:mb-10 lg:grid-cols-2 lg:items-end lg:gap-8">
            <div>
              <span className="font-['Poppins',sans-serif] text-[10px] font-semibold uppercase tracking-[0.2em] text-indigo-600 sm:text-xs">
                Our Core Values
              </span>

              <h3 className="mt-3 font-['Poppins',sans-serif] text-2xl font-semibold leading-tight tracking-tight text-slate-950 sm:mt-4 sm:text-3xl lg:text-4xl">
                The Principles Behind Every Project.
              </h3>
            </div>

            <p className="max-w-xl text-sm leading-7 text-slate-600 sm:text-base sm:leading-8 lg:ml-auto">
              Every project is guided by quality, practical design, reliable
              work and a commitment to understanding our clients.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {coreValues.map((value) => {
              const Icon = value.icon;

              return (
                <article
                  key={value.title}
                  className="group min-w-0 rounded-xl border border-slate-200 bg-slate-50 p-5 transition-colors duration-300 hover:border-indigo-200 hover:bg-white sm:rounded-2xl sm:p-6 lg:p-7"
                >
                  <div className="mb-5 flex h-10 w-10 items-center justify-center text-slate-700 sm:mb-6 sm:h-12 sm:w-12">
                    <Icon size={27} strokeWidth={1.5} aria-hidden="true" />
                  </div>

                  <h4 className="font-['Poppins',sans-serif] text-lg font-semibold text-slate-950 sm:text-xl">
                    {value.title}
                  </h4>

                  <p className="mt-2 text-sm leading-7 text-slate-600">
                    {value.desc}
                  </p>
                </article>
              );
            })}
          </div>
        </div>

        {/* OUR TEAM */}
        <div className="mb-14 grid grid-cols-1 gap-5 sm:mb-16 sm:gap-6 lg:grid-cols-2 lg:gap-8">
          <article className="min-w-0 rounded-2xl border border-slate-200 bg-white p-6 sm:rounded-3xl sm:p-8 md:p-10 lg:p-12">
            <div className="mb-5 flex h-11 w-11 items-center justify-center text-slate-700">
              <Users size={27} strokeWidth={1.5} aria-hidden="true" />
            </div>

            <span className="font-['Poppins',sans-serif] text-[10px] font-semibold uppercase tracking-[0.2em] text-indigo-600 sm:text-xs">
              Our Team
            </span>

            <h3 className="mt-3 font-['Poppins',sans-serif] text-2xl font-semibold leading-tight tracking-tight text-slate-950 sm:mt-4 sm:text-3xl lg:text-4xl">
              People Who Bring Your Ideas to Life.
            </h3>

            <p className="mt-4 text-sm leading-7 text-slate-600 sm:mt-6 sm:text-base sm:leading-8">
              Our work brings together design professionals, construction
              workers, project coordinators and skilled painters. We work
              towards a shared goal: making your space look better and work
              the way you need it to.
            </p>
          </article>

          {/* OUR APPROACH */}
          <article className="min-w-0 rounded-2xl border border-slate-200 bg-white p-6 sm:rounded-3xl sm:p-8 md:p-10 lg:p-12">
            <div className="mb-5 flex h-11 w-11 items-center justify-center text-slate-700">
              <Ruler size={27} strokeWidth={1.5} aria-hidden="true" />
            </div>

            <span className="font-['Poppins',sans-serif] text-[10px] font-semibold uppercase tracking-[0.2em] text-indigo-600 sm:text-xs">
              Our Approach
            </span>

            <h3 className="mt-3 font-['Poppins',sans-serif] text-2xl font-semibold leading-tight tracking-tight text-slate-950 sm:mt-4 sm:text-3xl lg:text-4xl">
              Every Detail Matters.
            </h3>

            <p className="mt-4 text-sm leading-7 text-slate-600 sm:mt-6 sm:text-base sm:leading-8">
              We plan before starting work, coordinate the different stages
              and check the final results. From the layout of a room to the
              finishing of a wall, we pay attention to the details that make
              a difference.
            </p>

            <ul className="mt-5 space-y-3">
              {operatingPrinciples.map((principle) => (
                <li
                  key={principle}
                  className="flex items-start gap-3 text-sm leading-6 text-slate-700"
                >
                  <CheckCircle2
                    size={18}
                    strokeWidth={1.6}
                    className="mt-0.5 shrink-0 text-indigo-600"
                    aria-hidden="true"
                  />
                  <span>{principle}</span>
                </li>
              ))}
            </ul>
          </article>
        </div>

        {/* LOCATION / MAPS */}
        <div className="mb-14 sm:mb-16">
          <div className="mb-7 grid grid-cols-1 gap-3 sm:mb-9 md:grid-cols-2 md:items-end md:gap-6">
            <div>
              <span className="font-['Poppins',sans-serif] text-[10px] font-semibold uppercase tracking-[0.2em] text-indigo-600 sm:text-xs">
                Find Us
              </span>

              <h3 className="mt-3 font-['Poppins',sans-serif] text-2xl font-semibold leading-tight tracking-tight text-slate-950 sm:text-3xl lg:text-4xl">
                Visit Our Locations.
              </h3>
            </div>

            <p className="max-w-xl text-sm leading-7 text-slate-600 sm:text-base md:ml-auto">
              Find Cube4Spaces for interior design and construction, or visit
              Paint Studio for painting and finishing services.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:gap-6 lg:grid-cols-2 lg:gap-8">
            {locations.map((location) => (
              <article
                key={location.name}
                className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white sm:rounded-3xl"
              >
                <div className="flex items-start gap-4 p-5 sm:p-6">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center text-slate-700">
                    <MapPin size={24} strokeWidth={1.5} aria-hidden="true" />
                  </div>

                  <div className="min-w-0">
                    <h4 className="font-['Poppins',sans-serif] text-lg font-semibold text-slate-950 sm:text-xl">
                      {location.name}
                    </h4>

                    <p className="mt-1 text-sm text-slate-600">
                      {location.subtitle}
                    </p>

                    <p className="mt-1 text-xs text-slate-500">
                      {location.address}
                    </p>
                  </div>

                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.name + " Coimbatore")}`}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Open ${location.name} in Google Maps`}
                    className="ml-auto inline-flex shrink-0 items-center gap-1 pt-1 text-xs font-semibold text-indigo-700 hover:text-indigo-900 sm:text-sm"
                  >
                    <span className="hidden sm:inline">Directions</span>
                    <ArrowUpRight size={17} strokeWidth={1.6} />
                  </a>
                </div>

                <div className="relative aspect-[4/3] w-full overflow-hidden border-t border-slate-100 sm:aspect-[16/9] lg:aspect-[5/3]">
                  <iframe
                    title={`${location.name} Google Maps location`}
                    src={location.mapSrc}
                    className="absolute inset-0 h-full w-full border-0"
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* FINAL CTA */}
        <div className="relative overflow-hidden rounded-2xl bg-slate-950 p-6 text-white sm:rounded-3xl sm:p-8 md:p-10 lg:p-14">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-16 -top-20 h-48 w-48 rounded-full bg-indigo-600/20 blur-3xl sm:h-72 sm:w-72"
          />

          <div className="relative grid grid-cols-1 items-center gap-7 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-10">
            <div className="min-w-0">
              <span className="font-['Poppins',sans-serif] text-[10px] font-semibold uppercase tracking-[0.2em] text-indigo-300 sm:text-xs">
                Ready to Transform Your Space?
              </span>

              <h3 className="mt-3 max-w-3xl font-['Poppins',sans-serif] text-2xl font-semibold leading-tight tracking-tight sm:mt-4 sm:text-3xl md:text-4xl lg:text-5xl">
                Tell Us What You Have in Mind.
              </h3>

              <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-300 sm:text-base sm:leading-8">
                Planning a new home, renovating your interiors, building a
                property or repainting your space? Cube4Spaces and Paint Studio
                are here to help you take the next step.
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-slate-300 sm:text-sm">
                <span>Cube4Spaces — Interior Design & Construction</span>
                <span className="text-indigo-400">|</span>
                <span>Paint Studio — Painting & Finishing Services</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => scrollToSection("contact")}
              className="inline-flex min-h-12 w-full items-center justify-center gap-3 rounded-lg bg-white px-6 py-3.5 font-['Poppins',sans-serif] text-sm font-semibold text-slate-950 transition-colors duration-300 hover:bg-indigo-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white sm:w-auto sm:min-w-[190px]"
            >
              Start Your Project
              <ArrowRight size={18} strokeWidth={1.7} />
            </button>
          </div>
        </div>

        {/* FOOTER */}
        <div className="mt-7 grid grid-cols-1 gap-2 border-t border-slate-200 pt-6 text-xs leading-6 text-slate-500 sm:mt-9 md:grid-cols-2 lg:grid-cols-3 lg:gap-4">
          <p>
            <span className="font-semibold text-slate-800">
              Cube4Spaces
            </span>
            {" "}— Interior Design & Construction
          </p>

          <p>
            <span className="font-semibold text-slate-800">
              Paint Studio
            </span>
            {" "}— Painting & Finishing Services
          </p>

          <p className="lg:text-right">
            Serving Coimbatore and nearby areas.
          </p>
        </div>
      </div>
    </section>
  );
}
